import type { ToolCall } from '@kanbu/schema';
import { uiActionOf } from '@kanbu/schema/contracts';
import {
  ChatRole,
  type SourcesDisplayMode,
  type SourcesFormat,
} from '@kanbu/schema/enums';
import { sortMessagesChronologically } from '@kanbu/shared';
import { motion, AnimatePresence } from 'motion/react';
import {
  type ComponentType,
  Fragment,
  type ReactNode,
  memo,
  useMemo,
} from 'react';

import type { RenderUnknownTool, ToolRegistry } from '../../tools';
import type { ChatMessageItem, MessagesGroup } from '../../types';
import { AdministratorMessage } from '../message/administrator-message';
import { AssistantMessage } from '../message/assistant-message';
import type { SharedMessageProps } from '../message/message';
import { StatusMessage } from '../message/status-message';
import { UserMessage } from '../message/user-message';

export type ChatRendererRoleConfig = Pick<
  SharedMessageProps,
  'variant' | 'position'
>;

export type ChatRendererRolesConfig = Record<
  Exclude<ChatRole, ChatRole.Status>,
  ChatRendererRoleConfig
>;

export interface ChatRendererProps {
  rolesConfig?: ChatRendererRolesConfig;
  agentName?: string;
  agentAvatar?: string;
  messages: ChatMessageItem[];
  /** Visitor widget only; preserve automatic actions in admin history. */
  hideUiActions?: boolean;
  toolRegistry?: ToolRegistry;
  renderUnknownTool?: RenderUnknownTool;
  className?: string;
  isFetching?: boolean;
  messageGroupingThreshold?: number;
  children?: ReactNode;
  renderAfterGroup?: (group: MessagesGroup, index: number) => ReactNode;
  shouldRenderFeedback?: (group: MessagesGroup) => boolean;
  showToolActivity?: boolean;
  /** Start the activity trail expanded while the agent works. */
  expandToolActivity?: boolean;
  /** Admin inbox: render every tool call via the full inspector, no trail. */
  debugToolCalls?: boolean;
  /** Admin inbox: attach the raw call/result inspector under each trail step. */
  renderTrailStepInspector?: (toolCall: ToolCall) => ReactNode;
  renderSources?: (message: ChatMessageItem) => React.ReactNode;
  sourcesFormat?: SourcesFormat;
  sourcesDisplayMode?: SourcesDisplayMode;
  /** Render `[cN]` markers as inline superscripts (default off: list only). */
  inlineCitations?: boolean;
  /** Show model reasoning blocks when present (default off). */
  showReasoning?: boolean;
  onRetry?: () => void;
  FeedbackComponent?: ComponentType<{
    message: ChatMessageItem;
  }>;
}

/**
 * Takes a messages and configuration and renders the data into
 * chat interface. Groups messages next to each other from same role
 * to a group within certain timestamp.
 */
export const ChatRenderer = memo(
  ({
    className = '',
    hideUiActions = false,
    messages,
    agentName,
    agentAvatar,
    toolRegistry,
    renderUnknownTool,
    rolesConfig,
    isFetching,
    FeedbackComponent,
    messageGroupingThreshold = 15 * 60 * 1000, // 15 minutes
    children,
    renderAfterGroup,
    shouldRenderFeedback,
    showToolActivity,
    expandToolActivity,
    debugToolCalls,
    renderTrailStepInspector,
    renderSources,
    sourcesFormat,
    sourcesDisplayMode,
    inlineCitations,
    showReasoning,
    onRetry,
  }: ChatRendererProps) => {
    /**
     * Creates a message groups by roles. We group multiple messages
     * from the same role into one group. This allows us to show only
     * one avatar, and only one timestamp.
     *
     * Since we use uuid v7 which is time based, we can sort the messages
     * by id to get the correct order.
     */
    const groupedMessages = useMemo(() => {
      const sortedMessages = messages.slice().sort(sortMessagesChronologically);
      const allGroups = sortedMessages.reduce<MessagesGroup[]>(
        (acc, message, index) => {
          const prevMessage = sortedMessages[index - 1];
          const lastGroup = acc.at(-1);

          if (
            !prevMessage ||
            prevMessage.role !== message.role ||
            Math.abs(
              new Date(message.createdAt).getTime() -
                new Date(prevMessage.createdAt).getTime(),
            ) > messageGroupingThreshold
          ) {
            acc.push({
              id: message.id,
              messages: [message],
              role: message.role,
              createdAt: new Date(message.createdAt),
              isInitial: message.isInitial,
              isLast: false,
            });
          } else {
            lastGroup?.messages.push(message);
          }

          return acc;
        },
        [],
      );

      const groups = hideUiActions
        ? allGroups
            .map(group => ({
              ...group,
              messages: group.messages.filter(
                message =>
                  message.role !== ChatRole.User || !uiActionOf(message),
              ),
            }))
            .filter(group => group.messages.length > 0)
        : allGroups;

      // Set isLast to true for the last message in each group
      if (groups.length > 0) {
        groups.at(-1)!.isLast = true;
      }

      return groups;
    }, [messages, messageGroupingThreshold, hideUiActions]);

    return (
      <div
        className={`flex grow flex-col gap-3 [&>*:first-child]:mt-auto ${className}`}
      >
        <AnimatePresence mode='sync'>
          {groupedMessages.map((group, index) => (
            <Fragment key={group.id}>
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {group.role === ChatRole.User ? (
                  <UserMessage group={group} {...rolesConfig?.[group.role]} />
                ) : group.role === ChatRole.Assistant ? (
                  <AssistantMessage
                    group={group}
                    name={agentName}
                    avatar={agentAvatar}
                    toolRegistry={toolRegistry}
                    renderUnknownTool={renderUnknownTool}
                    showToolActivity={showToolActivity}
                    expandToolActivity={expandToolActivity}
                    debugToolCalls={debugToolCalls}
                    renderTrailStepInspector={renderTrailStepInspector}
                    renderSources={renderSources}
                    sourcesFormat={sourcesFormat}
                    sourcesDisplayMode={sourcesDisplayMode}
                    inlineCitations={inlineCitations}
                    showReasoning={showReasoning}
                    onRetry={onRetry}
                    isFetching={isFetching}
                    isActive={
                      isFetching && index === groupedMessages.length - 1
                    }
                    shouldRenderFeedback={shouldRenderFeedback}
                    FeedbackComponent={FeedbackComponent}
                    {...rolesConfig?.[group.role]}
                  />
                ) : group.role === ChatRole.Administrator ? (
                  <AdministratorMessage
                    group={group}
                    {...rolesConfig?.[group.role]}
                  />
                ) : group.role === ChatRole.Status ? (
                  <StatusMessage group={group} />
                ) : null}
              </motion.div>
              {renderAfterGroup?.(group, index)}
            </Fragment>
          ))}
          {children}
        </AnimatePresence>
      </div>
    );
  },
);
